import React, {
  useCallback, useEffect, useMemo, useReducer, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Button, Spin, message } from 'antd';

import ModalWrapper from './ModalWrapper';
import useReferralEntityMatches from '../../hooks/useReferralEntityMatches';
import {
  buildReferralReviewViewModel,
  initialReviewSelectionState,
  reviewSelectionReducer,
} from './linkReferralReviewPlan';
import './linkReferralModal.scss';

const formatDateOfBirth = (value) => {
  if (!value) return 'Not provided';

  const dateOnlyMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return `${month}/${day}/${year}`;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleDateString();
};

const formatDocumentDate = (value) => {
  if (!value) return 'Date unavailable';

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const pageLabel = (numPages) => {
  if (!numPages) return 'Pages unavailable';

  return `${numPages} ${Number(numPages) === 1 ? 'page' : 'pages'}`;
};

const UNKNOWN_REFERRER = 'Unknown referrer';

const buildSourceMeta = (document) => {
  const referrer = document.referrer && document.referrer !== UNKNOWN_REFERRER ? document.referrer : null;
  const detail = document.referrer_detail;

  if (referrer) {
    return {
      label: `From ${referrer}`,
      detail: detail ? `(${detail})` : null,
    };
  }

  if (detail) {
    return {
      label: `From ${detail}`,
      detail: null,
    };
  }

  return null;
};

const LinkReferralModal = ({ record, hideModal, onSuccess }) => {
  const {
    fetchMatches,
    combine,
    matches,
    sourceReferralEntity,
    loading,
    combining,
    error,
  } = useReferralEntityMatches();
  const [reviewSelection, dispatchReviewSelection] = useReducer(
    reviewSelectionReducer,
    initialReviewSelectionState,
  );
  const combineInFlightRef = useRef(false);
  const modalActiveRef = useRef(true);

  useEffect(() => () => {
    modalActiveRef.current = false;
  }, []);

  useEffect(() => {
    fetchMatches(record?.file_token);
  }, [fetchMatches, record?.file_token]);

  const viewModel = useMemo(() => buildReferralReviewViewModel({
    record,
    matches,
    sourceReferralEntity,
    selectedRecordTokens: reviewSelection.selectedRecordTokens,
  }), [matches, record, reviewSelection.selectedRecordTokens, sourceReferralEntity]);

  const {
    documents,
    documentGroups,
    selectedRecords,
    primaryReferral,
    destinationRecord,
    targetReferralSlug,
    selectedDocumentTokens,
    requestedPrimaryRecordToken,
    reviewedPrimaryRecordToken,
    hasUnsupportedReviewPlan,
    canLink,
    patientName,
    patientDOB,
  } = viewModel;

  const documentTokenSignature = documents.map((document) => document.file_token).join('|');
  const reviewedDocumentTokens = useMemo(
    () => documents.map((document) => document.file_token).filter(Boolean),
    [documentTokenSignature],
  );

  useEffect(() => {
    dispatchReviewSelection({ type: 'documentsChanged', documents });
  }, [documentTokenSignature]);

  const toggleDocument = useCallback((fileToken) => {
    dispatchReviewSelection({ type: 'toggleDocument', fileToken, documents });
  }, [documents]);

  const handleLink = useCallback(async (followUpAction) => {
    if (combineInFlightRef.current || combining) return;

    combineInFlightRef.current = true;
    let result;
    try {
      result = await combine({
        targetReferralSlug,
        reviewedDocumentTokens,
        selectedDocumentTokens,
        primaryRecordToken: requestedPrimaryRecordToken,
        reviewedPrimaryRecordToken,
      });
    } finally {
      combineInFlightRef.current = false;
    }
    if (!modalActiveRef.current) return;

    if (result.error) {
      const staleTokens = Array.isArray(result.staleTokens) ? result.staleTokens.filter(Boolean) : [];
      if (staleTokens.length > 0) {
        // Backend signaled that some records the modal still thought were
        // linked have been unlinked elsewhere. Drop them from the local
        // selection and refresh matches so the user can retry without
        // closing the modal.
        dispatchReviewSelection({ type: 'pruneStaleTokens', staleTokens, documents });
        fetchMatches(record?.file_token);
        message.warning({
          content: 'Some records were already unlinked elsewhere; please review and try again.',
        });
        return;
      }

      message.error({ content: result.error });
      return;
    }

    if (result.data?.warning) {
      message.warning({ content: result.data.warning });
    } else {
      message.success({ content: 'Referral linked successfully' });
    }

    onSuccess({
      followUpAction,
      destinationRecord,
      referralEntity: primaryReferral,
    });
  }, [
    combine,
    combining,
    destinationRecord,
    documents,
    fetchMatches,
    onSuccess,
    primaryReferral,
    requestedPrimaryRecordToken,
    reviewedPrimaryRecordToken,
    reviewedDocumentTokens,
    record?.file_token,
    selectedDocumentTokens,
    targetReferralSlug,
  ]);

  const handleHideModal = useCallback(() => {
    if (combineInFlightRef.current || combining) return;

    hideModal();
  }, [combining, hideModal]);

  const content = useMemo(() => {
    if (loading) {
      return (
        <div className="link-referral-modal__spinner">
          <Spin size="large" />
        </div>
      );
    }

    if (matches.length === 0) {
      return (
        <div>
          <div className="link-referral-modal__patient-summary">
            <div>
              <span>Patient</span>
              <strong>{patientName}</strong>
            </div>
            <div>
              <span>DOB</span>
              <strong>{formatDateOfBirth(patientDOB)}</strong>
            </div>
          </div>
          <p className="link-referral-modal__empty">No suggested matches for this patient</p>
        </div>
      );
    }

    return (
      <div className="link-referral-modal">
        <p className="link-referral-modal__subtitle">
          Review all records found for this patient and choose which documents to link together.
        </p>

        <div className="link-referral-modal__patient-summary">
          <div>
            <span>Patient</span>
            <strong>{patientName}</strong>
          </div>
          <span aria-hidden="true" className="link-referral-modal__patient-divider">·</span>
          <div>
            <span>DOB</span>
            <strong>{formatDateOfBirth(patientDOB)}</strong>
          </div>
        </div>

        <div className="link-referral-modal__documents" aria-label="Documents found for patient">
          {documentGroups.map((group, groupIndex) => {
            const accentClass = group.isCurrentBucket
              ? ' link-referral-modal__group--current'
              : ` link-referral-modal__group--accent-${groupIndex % 3}`;

            return (
              <div className={`link-referral-modal__group${accentClass}`} key={group.key}>
                <div className="link-referral-modal__group-header">
                  <span className="link-referral-modal__group-label">
                    {group.isCurrentBucket
                      ? 'This document (not yet linked)'
                      : `Existing referral · ${group.recordCount} ${group.recordCount === 1 ? 'record' : 'records'} · ${group.status || 'Linked'}`}
                  </span>
                  {group.isAlreadyLinked && (
                    <span className="link-referral-modal__badge link-referral-modal__badge--linked">
                      Already linked
                    </span>
                  )}
                </div>

                <div className="link-referral-modal__group-records">
                  {group.documents.map((document) => {
                    const isSelected = selectedRecords.has(document.file_token);
                    const sourceMeta = buildSourceMeta(document);
                    const destinationLabel = `${sourceMeta ? '' : 'To '}${document.to || 'Medsender'}`;
                    const destinationDetailLabel = document.to_detail ? `(${document.to_detail})` : null;
                    const pagesLabel = pageLabel(document.num_pages);
                    const dateLabel = formatDocumentDate(document.created_at);

                    return (
                      <div
                        className={`link-referral-modal__document${isSelected ? ' link-referral-modal__document--selected' : ''}`}
                        key={`${document.file_token}-${document.referral_slug || 'current'}`}
                      >
                        <button
                          aria-label={`${isSelected ? 'Exclude' : 'Include'} ${document.title || 'document'}`}
                          aria-pressed={isSelected}
                          className={`link-referral-modal__check${isSelected ? ' link-referral-modal__check--selected' : ''}`}
                          onClick={() => toggleDocument(document.file_token)}
                          disabled={combining}
                          type="button"
                        >
                          ✓
                        </button>
                        <div className="link-referral-modal__document-content">
                          <div className="link-referral-modal__document-title-row">
                            <strong>{document.title || 'Untitled document'}</strong>
                            {document.is_current && <em>Current record</em>}
                          </div>
                          <div className="link-referral-modal__document-meta">
                            {sourceMeta && (
                              <>
                                <span>
                                  {sourceMeta.label}
                                  {sourceMeta.detail && <small>{sourceMeta.detail}</small>}
                                </span>
                                <span aria-hidden="true" className="link-referral-modal__meta-separator">→</span>
                              </>
                            )}
                            <span>
                              {destinationLabel}
                              {destinationDetailLabel && <small>{destinationDetailLabel}</small>}
                            </span>
                            <span aria-hidden="true" className="link-referral-modal__meta-separator">·</span>
                            <span>{pagesLabel}</span>
                            <span aria-hidden="true" className="link-referral-modal__meta-separator">·</span>
                            <span>{dateLabel}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="link-referral-modal__actions">
          <h3>What should happen after linking?</h3>
          {hasUnsupportedReviewPlan && (
            <p className="link-referral-modal__warning" role="alert">
              This review includes more than one destination referral. Choose one referral to link at a time.
            </p>
          )}
          <div>
            <Button disabled={!canLink || loading || combining} loading={combining} onClick={() => handleLink('link_only')}>
              Link only
            </Button>
            <Button disabled={!canLink || loading || combining} loading={combining} onClick={() => handleLink('go_to_record')}>
              Link and go to primary record
            </Button>
            <Button disabled={!canLink || loading || combining} loading={combining} onClick={() => handleLink('send_to_chart')}>
              Link and send to chart
            </Button>
          </div>
        </div>
      </div>
    );
  }, [
    loading,
    matches,
    patientDOB,
    patientName,
    documentGroups,
    selectedRecords,
    canLink,
    combining,
    handleLink,
    hasUnsupportedReviewPlan,
    toggleDocument,
  ]);

  return (
    <ModalWrapper
      bodyStyle={{ maxHeight: '72vh', overflowY: 'auto' }}
      customContentStyle={{ top: 40 }}
      dismiss={combining ? null : 'Close'}
      disabled={combining}
      hideModal={handleHideModal}
      isLoading={combining}
      closable={!combining}
      modalTitle="Patient Record Review"
      width={900}
      wrapClassName="link-referral-modal-wrap"
    >
      <div>
        {error && !loading && (
          <p className="link-referral-modal__error" role="alert">
            {error}
          </p>
        )}
        {content}
      </div>
    </ModalWrapper>
  );
};

LinkReferralModal.propTypes = {
  record: PropTypes.shape({
    file_token: PropTypes.string,
    referral_entity_slug: PropTypes.string,
    patient_name: PropTypes.string,
    patient_dob: PropTypes.string,
    send_token: PropTypes.string,
    receiver_type: PropTypes.string,
    is_primary_referral_record: PropTypes.bool,
  }),
  hideModal: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

LinkReferralModal.defaultProps = {
  record: null,
};

export default LinkReferralModal;
